import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, TextField } from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Autocomplete, Select, Input, Hidden } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function MyForm({
  close,
  restProps,
  rendering,
  toggleDisabled,
  ...rest
}) {
  const formRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const { client: idcliente } = useSelector((state) => state.user.profile);
  const isEditing = !!rest.idproduto;

  // Request in URL
  const handleRequest = useCallback(
    async (method, url, message, params = {}) => {
      try {
        await api[method](url, params);
        close();

        toast.success(t(message));
      } catch (err) {
        err.map((e) => toast.error(e));
      }
    },
    [close]
  );

  // submit form
  const handleSubmit = useCallback(
    async (data) => {
      const { idcontrato } = restProps;

      try {
        const schema = Yup.object().shape({
          idproduto: isEditing
            ? Yup.string()
            : Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
          controlacotacontratocota: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          volumecontratocota: Yup.number()
            .transform((value, originalValue) =>
              originalValue === '' ? undefined : value
            )
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0)),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        toggleDisabled(true);
        setLoading(true);

        const params = {
          ...data,
          volumecontratocota: Number(data.volumecontratocota),
          idcliente,
          idcontrato,
        };

        if (!isEditing) {
          await handleRequest(
            'post',
            'contratoscotas',
            's.geral.sucessoinclusao',
            params
          );
        } else {
          await handleRequest(
            'put',
            'contratoscotas',
            's.geral.sucessoalteracao',
            {
              ...params,
              saldocontratocota: rest.saldocontratocota,
              anomescontratocota: rest.anomescontratocota,
            }
          );
        }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          formRef.current.setErrors(validationErrors);
        }
      } finally {
        toggleDisabled(false);
        setLoading(false);
      }
    },
    [handleRequest, idcliente, isEditing, rest, restProps, toggleDisabled]
  );

  const statusSimNao = [
    { label: t('s.geral.opcoes.nao'), value: 'N' },
    { label: t('s.geral.opcoes.sim'), value: 'S' },
  ];

  return (
    <S.Wrapper>
      <S.FormWrapper initialData={rest} onSubmit={handleSubmit} ref={formRef}>
        {isEditing ? (
          <>
            <TextField
              label={t('s.menu.configuracao.produto')}
              value={rest.nomeproduto || ''}
              disabled
              variant="outlined"
              size="small"
              fullWidth
            />

            <Hidden name="idproduto" />
          </>
        ) : (
          <Autocomplete
            name="idproduto"
            label="nomeproduto"
            url="produtos"
            unique="idproduto"
            title={t('s.menu.configuracao.produto')}
            excluded={!!rendering && rendering.map((r) => r.idproduto)}
          />
        )}

        <Select
          name="controlacotacontratocota"
          label={t('s.cadastro.contrato.cota')}
          options={statusSimNao}
        />

        <Input
          name="volumecontratocota"
          label={t('s.cadastro.contrato.volumecontratocota')}
          type="number"
          min={0}
        />
      </S.FormWrapper>

      <S.GroupButtons>
        <Button
          startIcon={<Check />}
          color="primary"
          variant="outlined"
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
        >
          {t('s.geral.botaoconfirmar')}
        </Button>

        <Button
          startIcon={<Close />}
          color="secondary"
          variant="outlined"
          onClick={() => close()}
          disabled={loading}
        >
          {t('s.geral.botaocancelar')}
        </Button>
      </S.GroupButtons>
    </S.Wrapper>
  );
}

MyForm.propTypes = {
  restProps: PropTypes.shape({
    idcontrato: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
  }).isRequired,
  close: PropTypes.func.isRequired,
  rendering: PropTypes.arrayOf(
    PropTypes.shape({
      idproduto: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
